import { useState, useEffect, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Button, Card, Field, Input } from "./ui";
import { CardSkeleton } from "./ui";

const EVENTS: { key: string; label: string }[] = [
  { key: "maintenanceDue", label: "Upcoming maintenance due" },
  { key: "paymentReceived", label: "Payment received" },
  { key: "paymentOverdue", label: "Payment overdue" },
  { key: "rentalReadyForPickup", label: "Rental ready for pickup" },
  { key: "rentalEndingSoon", label: "Rental ending soon" },
  { key: "newLead", label: "New lead from website" },
  { key: "tabExpiring", label: "Registration tab expiring" },
  { key: "licenseExpiring", label: "Driver license expiring" },
];
type Channel = { email: boolean; sms: boolean };
type Events = Record<string, Channel>;

const emptyEvents = (): Events => Object.fromEntries(EVENTS.map((e) => [e.key, { email: false, sms: false }]));

export function Settings() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => apiFetch<any>("/settings", { token }), enabled: !!token });

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [events, setEvents] = useState<Events>(emptyEvents());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const n = q.data?.notifications;
    if (n) {
      setEmail(n.email ?? "");
      setPhone(n.phone ?? "");
      const merged = emptyEvents();
      for (const ev of EVENTS) {
        const c = (n.events ?? {})[ev.key] ?? {};
        merged[ev.key] = { email: !!c.email, sms: !!c.sms };
      }
      setEvents(merged);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: any) => apiFetch("/settings", { method: "PUT", body: JSON.stringify(body), token }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); setSaved(true); setTimeout(() => setSaved(false), 2500); },
  });

  const test = useMutation({
    mutationFn: () => apiFetch<{ sent: boolean; to: string }>("/notifications/test", { method: "POST", token }),
  });

  const toggle = (k: string, ch: keyof Channel) =>
    setEvents((s) => {
      const cur = s[k] ?? { email: false, sms: false };
      return { ...s, [k]: { email: ch === "email" ? !cur.email : cur.email, sms: ch === "sms" ? !cur.sms : cur.sms } };
    });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ notifications: { email: email || undefined, phone: phone || undefined, events } });
  }

  if (q.isPending) return <div className="space-y-6 max-w-3xl"><PageHeader title="Settings" /><CardSkeleton lines={3} /><CardSkeleton lines={6} /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title="Settings" />
      <div className="rounded-2xl border border-warn/30 bg-warn/10 px-4 py-3 text-sm">
        <b>Text (SMS) isn’t active yet.</b> It’s pending carrier approval — you can set your SMS preferences now,
        but no texts will send until it’s enabled. <b>Email notifications work today.</b>
      </div>
      <form onSubmit={onSubmit} className="space-y-6">
        <Card>
          <div className="p-5 space-y-1">
            <h2 className="text-sm font-semibold">Where to send notifications</h2>
            <p className="text-sm text-ink-muted mb-3">Set the destinations, then choose which events go to email, text, or both.</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Notification email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@acctechai.com" /></Field>
              <Field label="Notification phone (SMS) — not active yet"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+16125550100" /></Field>
            </div>
          </div>
        </Card>

        <Card>
          <table className="w-full text-sm">
            <thead className="text-left text-ink-muted border-b border-black/5">
              <tr>
                <th className="px-4 py-3 font-medium">Notify me when…</th>
                <th className="px-4 py-3 font-medium text-center w-24">Email</th>
                <th className="px-4 py-3 font-medium text-center w-24">Text <span className="text-[10px] font-bold text-warn align-top">SOON</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {EVENTS.map((ev) => (
                <tr key={ev.key}>
                  <td className="px-4 py-3">{ev.label}</td>
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-[var(--accent,#d97706)]"
                      checked={events[ev.key]?.email ?? false} onChange={() => toggle(ev.key, "email")} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-[var(--accent,#d97706)]"
                      checked={events[ev.key]?.sms ?? false} onChange={() => toggle(ev.key, "sms")} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save preferences"}</Button>
          <button type="button" onClick={() => test.mutate()} disabled={test.isPending}
            className="rounded-lg border border-black/10 px-4 py-2 text-sm font-medium hover:bg-black/5 disabled:opacity-60">
            {test.isPending ? "Sending…" : "Send test email"}
          </button>
          {test.data?.sent && <span className="text-sm text-green-600">Test email sent to {test.data.to}.</span>}
          {test.error ? <span className="text-sm text-red-600">{(test.error as any)?.message ?? "Test failed."}</span> : null}
          {saved && <span className="text-sm text-green-600">Saved.</span>}
          {save.error ? <span className="text-sm text-red-600">{(save.error as any)?.message ?? "Save failed."}</span> : null}
        </div>
      </form>
    </div>
  );
}
